import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { FooterData, Project, Testimonial } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { SiteNavbar } from '@/components/generals/site-navbar'
import { FooterSplit } from '@/components/generals/footer-split'
import { HeroSection } from '@/components/landing/hero-section'
import { PortfolioSection } from '@/components/landing/portfolio-section'
import { TestimonialsSection } from '@/components/landing/testimonials-section'
import { ContactSection } from '@/components/landing/contact-section'
import { resolveAssetUrl } from '@/lib/links'

import {
    CONTENT_REVALIDATE_SECONDS,
    getFooterData,
    getProjects,
    getTestimonials
} from '@/lib/content'

import {
    CONTACT_HREF,
    HERO_IMAGE_URL,
    HOME_NAV_LINKS,
    PORTFOLIO_HREF
} from '@/constants/site'

interface HomeProps {
    heroImage: string | null
    projects: Array<Project>
    testimonials: Array<Testimonial>
    footer: FooterData
}

// Props default to empty content so the page never crashes if it renders before
// its data has arrived (for example right after a live code update in the preview).
const Home: FC<Partial<HomeProps>> = ({
    heroImage = null,
    projects = [],
    testimonials = [],
    footer
}): ReactNode => {
    return (
        <Layout
            header={(
                <SiteNavbar
                    variant='bar'
                    links={HOME_NAV_LINKS}
                    currentHref='/'
                    secondaryAction={{ label: 'Projects', href: PORTFOLIO_HREF }}
                    primaryAction={{ label: 'Contact', href: CONTACT_HREF }}
                />
            )}
            footer={<FooterSplit year={footer?.year} socialLinks={footer?.socialLinks} />}
        >
            <Seo ogImage={heroImage ?? undefined} />

            <HeroSection image={heroImage} />
            <PortfolioSection projects={projects} />
            <TestimonialsSection testimonials={testimonials} />
            <ContactSection />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    const [projects, testimonials, footer] = await Promise.all([
        getProjects(),
        getTestimonials(),
        getFooterData()
    ])

    return {
        props: {
            heroImage: resolveAssetUrl(HERO_IMAGE_URL),
            projects,
            testimonials,
            footer
        },
        revalidate: CONTENT_REVALIDATE_SECONDS
    }
}

export default Home
